import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3, TrendingUp, Activity } from 'lucide-react';
import { Translations } from '../types';
import SpotlightCard from './SpotlightCard';

interface DemoProps {
  t: Translations;
}

export const Demo: React.FC<DemoProps> = ({ t }) => {
  return (
    <section className="demo" aria-labelledby="demo-heading" id="demo">
      <div className="container">
        <motion.header
          className="demo-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 id="demo-heading" className="demo-title">{t.demo.title}</h2>
          <p className="demo-subtitle">{t.demo.subtitle}</p>
        </motion.header>

        <motion.div
          className="demo-content"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="demo-visual">
            <div className="demo-video" aria-label="Product demo video">
              <video
                className="demo-video-player"
                controls
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                crossOrigin="anonymous"
              >
                {/* Prefer MP4 (H.264/AAC) for widest browser support */}
                <source
                  src="https://ap-landing-page-video.s3.ap-northeast-1.amazonaws.com/landingpage%20copy.mp4"
                  type="video/mp4"
                />
                <source
                  src="https://ap-landing-page-video.s3.ap-northeast-1.amazonaws.com/landingpage.mov"
                  type="video/quicktime"
                />
                {/* Local fallback if the S3 asset is unreachable */}
                <source src="/landingpage.mov" type="video/quicktime" />
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="demo-cards" role="group" aria-label="产品演示卡片">
              <SpotlightCard 
                className="demo-card primary" 
                spotlightColor="rgba(0, 255, 157, 0.08)"
              >
                <div className="card-header">
                  <BarChart3 size={24} aria-hidden="true" />
                  <span className="live-indicator">
                    <Activity size={16} aria-hidden="true" />
                    {t.demo.liveAnalysis}
                  </span>
                </div>
                <div className="chart-placeholder" aria-label="实时交易图表">
                  <div className="chart-line"></div>
                  <div className="chart-bars" role="img" aria-label="交易数据柱状图">
                    <div className="bar" style={{height: '60%'}}></div>
                    <div className="bar" style={{height: '80%'}}></div>
                    <div className="bar" style={{height: '45%'}}></div>
                    <div className="bar" style={{height: '90%'}}></div>
                    <div className="bar" style={{height: '70%'}}></div>
                  </div>
                </div>
              </SpotlightCard>

              <SpotlightCard 
                className="demo-card secondary" 
                spotlightColor="rgba(0, 255, 157, 0.1)"
              >
                <div className="card-header">
                  <TrendingUp size={24} aria-hidden="true" />
                  <span className="performance-badge" aria-label="策略表现">+23.7%</span>
                </div>
                <div className="performance-text">{t.demo.performance}</div>
                <div className="strategy-indicators" role="img" aria-label="策略指标状态">
                  <div className="indicator green" aria-label="指标1: 良好"></div>
                  <div className="indicator green" aria-label="指标2: 良好"></div>
                  <div className="indicator yellow" aria-label="指标3: 中等"></div>
                  <div className="indicator green" aria-label="指标4: 良好"></div>
                </div>
              </SpotlightCard>

              <SpotlightCard 
                className="demo-card tertiary" 
                spotlightColor="rgba(0, 212, 255, 0.1)"
              >
                <div className="pattern-recognition">
                  <div className="pattern-grid" role="img" aria-label="模式识别网格">
                    {Array.from({length: 16}).map((_, i) => (
                      <div 
                        key={i} 
                        className={`pattern-cell ${i % 3 === 0 ? 'active' : ''}`}
                      ></div>
                    ))}
                  </div>
                  <div className="pattern-label">Pattern Detected</div>
                </div>
              </SpotlightCard>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}; 
